/**
 * 认证相关 API
 */

import { appConfig } from '../config';

// API 基础配置
const API_BASE_URL = appConfig.api.baseUrl;
const TENANT_ID = appConfig.api.tenantId;

// 类型定义
export interface RegisterRequest {
  mobile: string;
  password: string;
  source: string;
}

export interface LoginRequest {
  mobile: string;
  password: string;
}

export interface AuthResponse {
  code: number;
  msg: string;
  data: {
    userId: number;
    accessToken: string;
    refreshToken: string;
    expiresTime: number;
    openid: string | null;
  };
}

/**
 * 注册
 */
export const register = async (data: RegisterRequest): Promise<AuthResponse> => {
  const response = await fetch(`${API_BASE_URL}/member/auth/register`, {
    method: 'POST',
    headers: {
      'tenant-id': TENANT_ID,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * 登录
 */
export const login = async (data: LoginRequest): Promise<AuthResponse> => {
  const response = await fetch(`${API_BASE_URL}/member/auth/login`, {
    method: 'POST',
    headers: {
      'tenant-id': TENANT_ID,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

/**
 * Token 管理
 */
export const tokenManager = {
  // 存储 token
  setToken: (accessToken: string, refreshToken: string, expiresTime: number) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('expiresTime', expiresTime.toString());
  },

  // 获取 access token
  getAccessToken: (): string | null => {
    return localStorage.getItem('accessToken');
  },

  // 获取 refresh token
  getRefreshToken: (): string | null => {
    return localStorage.getItem('refreshToken');
  },

  // 检查 token 是否过期
  isTokenExpired: (): boolean => {
    const expiresTime = localStorage.getItem('expiresTime');
    if (!expiresTime) return true;
    return Date.now() > parseInt(expiresTime);
  },

  // 清除 token
  clearToken: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expiresTime');
  },

  // 获取认证头
  getAuthHeader: (): Record<string, string> => {
    const token = tokenManager.getAccessToken();
    if (!token) return {};
    return {
      'Authorization': `Bearer ${token}`,
      'tenant-id': TENANT_ID,
    };
  },
};

/**
 * 通用 API 请求函数（带认证）
 */
export const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const headers = {
    ...tokenManager.getAuthHeader(),
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // 如果返回 401，token 可能过期
  if (response.status === 401) {
    tokenManager.clearToken();
    // 可以触发重新登录
    window.location.href = '/';
  }

  return response;
};

